import { Button } from '@progress/kendo-react-buttons';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { useMemo } from 'react';
import { Menu, MenuItem } from '../../../components/common/menu/DropdownMenu';
import { DrawingRectangleOutline, ExternalLinkOutline, PlusOutline } from '../../../icons/icons';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { addPanelToWindow, createWindow, setWorkspacePanelVisibility } from '../../state/slices/workspace-slice';
import CustomPopover from '../layout/CustomPopover';
import { getMaxPanelCount, useMultiWindowContext } from './context/multi-window-context';
import { WorkspaceWindowPanelType } from './types';

export function OpenInMultiWindowButton(props: { type: WorkspaceWindowPanelType; icon?: SVGIcon; airportId?: string }) {
    const multiWindowContext = useMultiWindowContext();
    if (multiWindowContext.active) {
        return null;
    }

    return <_OpenInMultiWindowButton {...props} />;
}

function _OpenInMultiWindowButton(props: { type: WorkspaceWindowPanelType; icon?: SVGIcon; airportId?: string }) {
    const windows = useAppSelector((x) => x.workspace.windows);
    const totalPanelCount = useMemo(
        // Count this panel occurrence across all windows
        () => windows.reduce((p, n) => p + n.panels.reduce((p, n) => (n.type === props.type ? p + 1 : p), 0), 0),
        [windows, props.type]
    );
    const maxPanelCount = getMaxPanelCount(props.type);

    const handleAfterPanelAdded = () => {
        if (props.type === WorkspaceWindowPanelType.fleetMonitor) {
            dispatch(setWorkspacePanelVisibility({ panel: 'fleetMonitor', visible: false }));
        } else if (props.type === WorkspaceWindowPanelType.airportMonitor) {
            dispatch(setWorkspacePanelVisibility({ panel: 'airportMonitor', visible: false }));
        } else if (props.type === WorkspaceWindowPanelType.eventList) {
            dispatch(setWorkspacePanelVisibility({ panel: 'eventList', visible: false }));
        } else if (props.type === WorkspaceWindowPanelType.mapLegend) {
            dispatch(setWorkspacePanelVisibility({ panel: 'legend', visible: false }));
        } else if (props.type === WorkspaceWindowPanelType.routeAnalyzer) {
            dispatch(setWorkspacePanelVisibility({ panel: 'routeAnalyzer', visible: false }));
        }
    };

    const handleOpenNewWindow = async () => {
        if ('getScreenDetails' in window) {
            const { state } = await navigator.permissions.query({ name: 'window-management' as any });
            if (state === 'prompt') {
                await window.getScreenDetails();
            }
        }

        dispatch(createWindow({ initialPanel: props.type, airportId: props.airportId }));
        handleAfterPanelAdded();
    };
    const dispatch = useAppDispatch();

    if (totalPanelCount >= maxPanelCount) {
        return null;
    }

    if (windows.length === 0) {
        return (
            <Button
                svgIcon={props.icon ?? ExternalLinkOutline}
                title="Open in new window"
                fillMode="flat"
                className="button-small open-in-external-window-btn"
                onClick={() => handleOpenNewWindow()}
            />
        );
    }

    return (
        <CustomPopover
            trigger={
                <Button
                    svgIcon={props.icon ?? ExternalLinkOutline}
                    title="Open in new window"
                    fillMode="flat"
                    className="button-small open-in-external-window-btn"
                />
            }>
            <Menu label="">
                {windows.map((window) => (
                    <MenuItem
                        icon={DrawingRectangleOutline}
                        key={window.id}
                        className="p-2"
                        label={`Add to ${window.name}`}
                        onClick={() => {
                            dispatch(addPanelToWindow({ windowId: window.id, panel: props.type, airportId: props.airportId }));
                            handleAfterPanelAdded();
                        }}
                    />
                ))}
                <MenuItem icon={PlusOutline} label="Create new window" onClick={handleOpenNewWindow} />
            </Menu>
        </CustomPopover>
    );
}
